import InfoLabel from '@/components/InfoLabel';
import QuestionHeader from '@/components/QuestionHeader';
import { BOOKER_EMAIL_QUESTION_ID, BOOKER_PHONE_QUESTION_ID_CHANGA } from '@/constants/constants';
import IQuestion, { IQuestionLocation, IQuestionRadio, IQuestionType } from '@/types/IQuestion';
import { AccordionItem, AccordionPanel, FormControl, Input, InputGroup, InputRightAddon } from '@chakra-ui/react';
import { Select, SingleValue } from 'chakra-react-select';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export type IQuestionBasicProps = Exclude<IQuestion, IQuestionRadio | IQuestionLocation> & {
	isLoading: boolean;
	onChange: (q: IQuestion) => void;
	onDelete: (q: { id: string }) => void;
};

export default function QuestionBasic(props: IQuestionBasicProps) {
	const { t } = useTranslation();

	const options = useGetOptions(props.id);

	const form = useForm({
		defaultValues: {
			id: props.id,
			label: props.label,
			type: props.type,
			// TODO: El fucking odin no implemento esta movida
			required: true,
		},
	});

	/**
	 * Callback executed every time something in the question is changed.
	 */
	const handleChange = form.handleSubmit((v) => {
		props.onChange({
			id: v.id,
			label: v.label,
			type: v.type,
			required: v.required,
		});
	});

	function handleQuestionTypeChange(e: SingleValue<{ label: string; value: IQuestionType }>) {
		if (!e) {
			return;
		}
		// @ts-ignore We can change the question type
		form.setValue('type', e.value);
		handleChange();
	}

	return (
		<AccordionItem>
			<QuestionHeader {...props} />
			<AccordionPanel pb={4}>
				<FormControl>
					<InfoLabel
						text={t('Id')}
						help={t('Autofill the form values adding ttq[id]=value as search parameters on the booking page URL.')}
					/>
					<InputGroup size="sm">
						<Input disabled defaultValue={form.getValues('id')} readOnly size="sm" />
						<InputRightAddon
							_hover={{ color: 'blue.600', textDecoration: 'underline' }}
							cursor="pointer"
							onClick={() => {
								navigator.clipboard.writeText(`ttq[${form.getValues('id')}]=`);
								toast.success(t('Copied to clipboard') as string);
							}}
						>
							ttq[{form.getValues('id')}]=
						</InputRightAddon>
					</InputGroup>
				</FormControl>

				<FormControl mt="2">
					<InfoLabel text={t('Label')} help={t('Tell the booker which data to introduce. Name, email, phone...')} />
					<Input isDisabled={props.isLoading} size="sm" {...form.register('label')} onBlur={handleChange} />
				</FormControl>

				<FormControl>
					<InfoLabel mt="2" text={t('Type')} help={t('The question type')} />
					<Select
						isDisabled={props.isLoading}
						{...form.register('type')}
						defaultValue={options.find((op) => op.value === form.getValues('type'))}
						menuPosition="fixed"
						onChange={handleQuestionTypeChange}
						options={options}
						size="sm"
					/>
				</FormControl>
			</AccordionPanel>
		</AccordionItem>
	);
}

/**
 * Si la pregunta es el email o el teléfono, no permitimos cambiar el tipo de pregunta porque hay una lógica extra que ha generado esa pregunta.
 * BOOKER_EMAIL_QUESTION_ID
 * 	Es la pregunta "email" que genera timetime cuando se crea una página de reservas para notificar al booker. Es obligatoria en la app.
 * BOOKER_PHONE_QUESTION_ID_CHANGA
 * 	Es una pregunta phone que se genera automáticamente cuando la localización es "phone".
 *
 */
function useGetOptions(questionId: string): Array<{ label: string; value: IQuestionType }> {
	const { t } = useTranslation();

	switch (questionId) {
		case BOOKER_PHONE_QUESTION_ID_CHANGA:
			return [{ label: t('Phone number') as string, value: 'PHONE' }];
		case BOOKER_EMAIL_QUESTION_ID:
			return [{ label: t('Email') as string, value: 'EMAIL' }];
		default:
			return [
				{ label: t('Text') as string, value: 'TEXT' },
				{ label: t('Phone number') as string, value: 'PHONE' },
				{ label: t('Email') as string, value: 'EMAIL' },
				{ label: t('Image') as string, value: 'IMAGE' },
			];
	}
}
